import { getCostsAnalysis } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";

// Drill-down de Análisis de Costos para un mes específico. Espejo de
// salesAnalysis: el tab Costos lo abre al clickear una celda mensual de la
// tabla de evolución. Módulo independiente para no acoplar con el flujo de
// Ventas — el shape del payload difiere (statement jerárquico con márgenes +
// ranking de contrapartes en lugar de stores/KPIs/clientes).

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const state = () => ({
    /** YYYY-MM de la celda que abrió el modal. Null cuando está cerrado. */
    month: null,
    /** Cuenta clickeada (level_3). Null = mes completo. Sólo recorta el ranking. */
    account: null,
    /** Categoría dinámica (no usada hoy por Costos; reservada para paridad). */
    groupBy: null,
    /**
     * Filtro multi-select por categoría: { tiendas: ["T1"], marcas: [...] }.
     * Recorta statement + ranking. Categoría sin entries = sin filtro.
     */
    selectedItems: {},
    /**
     * Payload del backend (frozen para opt-out del observer profundo de Vue 2).
     * @type {{
     *   month: string,
     *   account: string | null,
     *   period_label: string,
     *   category_label: string | null,
     *   headline: { amount: number, mom: number | null, yoy: number | null },
     *   statement: {
     *     sales_total: number,
     *     total_costs: number,
     *     groups: Array<{
     *       level_2: string,
     *       amount: number,
     *       children: Array<{ account: string, amount: number }>,
     *       margin: { label: string, amount: number, pct: number | null },
     *     }>,
     *   },
     *   counterparts_ranking: Array<{
     *     name: string, amount: number, share: number, counterpart_type: string,
     *   }>,
     * } | null}
     */
    data: null,
    isLoading: false,
    error: null,
    /** Filtro de texto del ranking de contrapartes (case-insensitive). */
    counterpartsQuery: "",
});

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

const getters = {
    /**
     * Ranking de contrapartes filtrado por `counterpartsQuery`. Devuelve la
     * lista completa cuando la query está vacía.
     */
    filteredCounterparts: (state) => {
        const ranking = state.data?.counterparts_ranking ?? [];
        const q = state.counterpartsQuery.trim().toLowerCase();
        if (!q) return ranking;
        return ranking.filter((c) => c.name.toLowerCase().includes(q));
    },
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const mutations = {
    SET_CONTEXT(state, { month, account, groupBy, selectedItems }) {
        state.month = month;
        state.account = account ?? null;
        state.groupBy = groupBy ?? null;
        state.selectedItems = Object.freeze(selectedItems ?? {});
    },
    SET_SELECTED_ITEMS(state, selectedItems) {
        state.selectedItems = Object.freeze(selectedItems ?? {});
    },
    SET_DATA(state, payload) {
        state.data = payload;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_COUNTERPARTS_QUERY(state, query) {
        state.counterpartsQuery = query;
    },
    RESET(state) {
        state.month = null;
        state.account = null;
        state.groupBy = null;
        state.selectedItems = {};
        state.data = null;
        state.error = null;
        state.counterpartsQuery = "";
    },
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const actions = {
    async fetchCostsAnalysis({ commit }, { month, account = null, groupBy = null, selectedItems = null }) {
        commit("SET_CONTEXT", { month, account, groupBy, selectedItems });
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await getCostsAnalysis(month, account, groupBy, selectedItems);
            commit("SET_DATA", Object.freeze(response.data));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[costsAnalysis]", silent: true });
            commit("SET_ERROR", extractErrorMessage(error, "Error al cargar el análisis de costos."));
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
